// Runtime Google Fonts loader with an Electron disk cache.
//
// ensureFont(family, weights): resolves once the family is registered in
// document.fonts (canvas text does NOT trigger CSS font loading by itself).
// Flow per family: bundled/system → editorFonts path / no-op; Google →
// fetch css2 stylesheet (keyless), parse @font-face blocks, load each woff2
// (disk cache first — cached families work fully offline), register
// FontFace objects with weight/style/unicode-range descriptors.
//
// Failure is soft: a family that can't load resolves { ok: false } and the
// caller keeps the current font (callers toast; canvas falls back).
//
// fabric v6 interplay: fabric caches per-family glyph widths
// (cache.charWidthsCache). Any text measured BEFORE a face registers has
// fallback-font metrics cached, and the callers' initDimensions() re-measure
// would read that stale cache — so the family's cache entry is dropped here
// the moment its faces land in document.fonts, before ensureFont resolves.
import { API_BASE } from '../../../../config.js';
import { cache } from 'fabric';
import { EDITOR_FONTS, loadEditorFonts } from '../editorFonts';
import { getFontEntry } from './fontCatalog';

const familyPromises = new Map(); // `${family}|${weightsKey}` -> Promise<{ok}>
const registeredFamilies = new Set();

function isLocalFamily(family) {
  return EDITOR_FONTS.some((f) => f.family === family);
}

async function cacheGet(key) {
  try {
    const res = await null /* web: the browser HTTP cache already persists woff2 bytes */;
    if (res?.success && res.data) {
      const bin = atob(res.data);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return bytes.buffer;
    }
  } catch (_) { /* cache miss */ }
  return null;
}

function cachePut(key, arrayBuffer) {
  try {
    const bytes = new Uint8Array(arrayBuffer);
    let bin = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    /* web: no font cache write — the browser HTTP cache covers it */
  } catch (_) { /* best-effort */ }
}

async function fetchTextCached(key, url) {
  const cached = await cacheGet(key);
  if (cached) return new TextDecoder().decode(cached);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`css2 ${res.status}`);
  const text = await res.text();
  cachePut(key, new TextEncoder().encode(text).buffer);
  return text;
}

async function fetchBinaryCached(url) {
  const key = `woff2:${url}`;
  const cached = await cacheGet(key);
  if (cached) return cached;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`font fetch ${res.status}`);
  const buf = await res.arrayBuffer();
  cachePut(key, buf);
  return buf;
}

// Parse the css2 response into font-face descriptors
function parseFontFaces(cssText) {
  const faces = [];
  const blocks = cssText.match(/@font-face\s*{[^}]*}/g) || [];
  for (const block of blocks) {
    const url = block.match(/url\((https:[^)]+\.woff2)\)/)?.[1];
    if (!url) continue;
    faces.push({
      url,
      weight: block.match(/font-weight:\s*([\d\s]+);/)?.[1]?.trim() || '400',
      style: block.match(/font-style:\s*(\w+);/)?.[1] || 'normal',
      unicodeRange: block.match(/unicode-range:\s*([^;]+);/)?.[1]?.trim(),
    });
  }
  return faces;
}

async function loadGoogleFamily(family, weights) {
  const weightsKey = [...new Set(weights)].sort((a, b) => a - b).join(';');
  const familyParam = family.replace(/ /g, '+');

  let cssText = null;
  // Try with the weight axis; some single-weight families 400 on unknown
  // weights — retry bare (regular only).
  const attempts = [
    `${API_BASE}/design/fonts/css2?family=${familyParam}:wght@${weightsKey}&display=swap`,
    `${API_BASE}/design/fonts/css2?family=${familyParam}&display=swap`,
  ];
  for (const url of attempts) {
    try {
      cssText = await fetchTextCached(`css:${url}`, url);
      break;
    } catch (_) { /* try next */ }
  }
  if (!cssText) return { ok: false };

  const faces = parseFontFaces(cssText);
  if (faces.length === 0) return { ok: false };

  const results = await Promise.allSettled(faces.map(async (face) => {
    const buf = await fetchBinaryCached(face.url);
    const descriptors = { weight: face.weight, style: face.style };
    if (face.unicodeRange) descriptors.unicodeRange = face.unicodeRange;
    const ff = new FontFace(family, buf, descriptors);
    await ff.load();
    document.fonts.add(ff);
  }));

  const okCount = results.filter((r) => r.status === 'fulfilled').length;
  if (okCount > 0) {
    registeredFamilies.add(family);
    // Drop stale fallback-font measurements (see header) so the callers'
    // initDimensions()/setCoords() re-measure with the real face.
    cache.clearFontCache(family);
  }
  return { ok: okCount > 0 };
}

export function ensureFont(family, weights) {
  if (!family) return Promise.resolve({ ok: false });
  if (isLocalFamily(family)) {
    // Bundled faces go through the existing @font-face loader; system
    // staples need nothing.
    const entry = EDITOR_FONTS.find((f) => f.family === family);
    return entry?.bundled ? loadEditorFonts().then(() => ({ ok: true })) : Promise.resolve({ ok: true });
  }
  const entry = getFontEntry(family);
  const wanted = weights?.length
    ? weights
    : (entry?.weights || [400, 700]).filter((w) => w === 400 || w === 700);
  const key = `${family}|${[...new Set(wanted)].sort((a, b) => a - b).join(';')}`;
  if (!familyPromises.has(key)) {
    familyPromises.set(key, loadGoogleFamily(family, wanted.length ? wanted : [400]));
  }
  return familyPromises.get(key);
}

// Ensure every family a design references before it loads, so text measures
// and wraps with the real faces (re-measure after is handled by the caller).
export function ensureFontsForDesign(designData) {
  const families = new Set();
  const objects = designData?.objects || [];
  for (const obj of objects) {
    if (obj?.fontFamily) families.add(obj.fontFamily);
    // groups nest objects
    if (Array.isArray(obj?.objects)) {
      for (const child of obj.objects) {
        if (child?.fontFamily) families.add(child.fontFamily);
      }
    }
  }
  if (families.size === 0) return Promise.resolve();
  return Promise.allSettled([...families].map((f) => ensureFont(f)));
}

export function isFontRegistered(family) {
  return isLocalFamily(family) || registeredFamilies.has(family);
}
